import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FreeResourcesBar from '../components/FreeResourcesBar';
import { CheckCircle, Award, Clock, Users, Calendar, Download, ChevronDown, ChevronUp } from 'lucide-react';

const AICoursePage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const syllabus = [
    { week: "Module 1", title: "AI Fundamentals & Overview", topics: ["What is AI and why it matters", "AI vs Machine Learning vs Deep Learning", "Real-world AI applications", "Ethics and responsible AI"] },
    { week: "Module 2", title: "Machine Learning Basics", topics: ["Supervised vs Unsupervised learning", "Common ML algorithms", "Training and testing models", "Model evaluation metrics"] },
    { week: "Module 3", title: "Natural Language Processing", topics: ["Text processing and analysis", "Sentiment analysis", "Named Entity Recognition", "Building chatbots and virtual assistants"] },
    { week: "Module 4", title: "Computer Vision & Image Recognition", topics: ["Image classification basics", "Object detection", "Facial recognition systems", "Practical applications in business"] },
    { week: "Module 5", title: "AI Tools & Platforms", topics: ["AWS AI Services (SageMaker, Bedrock)", "Azure AI & Cognitive Services", "Google Cloud AI Platform (Vertex AI)", "Hands-on labs and certifications"] },
    { week: "Module 6", title: "AI in Business & Career Development", topics: ["AI strategy and implementation", "Building your AI portfolio", "Resume and interview preparation", "Certification exam preparation"] }
  ];

  const faqs = [
    {
      id: 1,
      question: 'Do I need a technical background to enroll?',
      answer: 'No coding background is required. This program is designed for beginners who want to learn AI fundamentals and get certified as AI Practitioners across AWS, Azure, and GCP. We start with basics and build up to certification-level knowledge.'
    },
    {
      id: 2,
      question: 'What certifications will I be prepared for?',
      answer: 'This course prepares you for three major AI Practitioner certifications: AWS AI Practitioner, Microsoft Azure AI Fundamentals, and Google Cloud AI Platform certification. You\'ll learn the concepts and skills needed to pass all three exams.'
    },
    {
      id: 3,
      question: 'How is the course delivered?',
      answer: 'The course is delivered 100% online with live virtual sessions, self-paced learning materials, and hands-on labs. You\'ll have access to cloud platforms for practical experience and lifetime access to course content.'
    },
    {
      id: 4,
      question: 'What projects will I build?',
      answer: 'You\'ll build 4+ portfolio-ready projects including an AI chatbot, image classification system, sentiment analysis tool, and automated workflow assistant. All projects use real cloud AI services from AWS, Azure, or GCP.'
    },
    {
      id: 5,
      question: 'What kind of support will I receive?',
      answer: 'You\'ll receive comprehensive support including live mentorship sessions, access to a private community, regular feedback on projects, career guidance, and certification exam preparation. Our team is always available to help.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Header />
      <FreeResourcesBar />

      {/* Hero Section - Dark Theme */}
      <section className="pt-24 pb-16 bg-[#0B1120] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #4F46E5 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-black px-6 py-2 rounded-full font-bold mb-6 shadow-lg">
              <Award className="w-5 h-5" />
              <span>AI Career Launchpad</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight text-white">
              AI Career Launchpad — Your Path to AI Mastery
            </h1>

            {/* Description */}
            <p className="text-xl md:text-2xl text-[#D1D5DB] mb-8 leading-relaxed">
              Kick-start your career with hands-on AI, Cloud, and automation projects. Master AI fundamentals and get certified across AWS, Azure, and GCP while building a portfolio that gets you hired.
            </p>

            <p className="text-lg text-[#9CA3AF] mb-8">
              <strong>No coding background required | 100% online | Live mentorship included</strong>
            </p>

            {/* Key Info Badges */}
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-[#4F46E5]/30">
                <div className="flex items-center gap-2 text-[#D1D5DB]">
                  <Calendar className="w-5 h-5 text-[#F59E0B]" />
                  <span className="font-semibold">Starts November 4, 2024</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-[#4F46E5]/30">
                <div className="flex items-center gap-2 text-[#D1D5DB]">
                  <Clock className="w-5 h-5 text-[#F59E0B]" />
                  <span className="font-semibold">6-8 weeks | Self-paced</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-[#4F46E5]/30">
                <div className="flex items-center gap-2 text-[#D1D5DB]">
                  <Users className="w-5 h-5 text-[#F59E0B]" />
                  <span className="font-semibold">Limited to 50 students</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-7 py-3.5 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-semibold text-base rounded-xl transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(79,70,229,0.4)] min-w-[180px]"
              >
                Enroll Now
              </button>
              <button
                onClick={() => document.getElementById('syllabus')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-7 py-3 bg-transparent border-2 border-[#4F46E5] text-[#D1D5DB] hover:bg-[#4F46E5]/10 hover:text-white font-semibold text-base rounded-xl transition-all duration-300 min-w-[180px]"
              >
                View Curriculum
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Award, title: "3 CLOUD CERTIFICATIONS", desc: "Prepare for AWS, Azure & GCP AI certifications" },
              { icon: Clock, title: "6-8 WEEKS DURATION", desc: "Flexible, self-paced learning with live support" },
              { icon: Users, title: "LIVE MENTORSHIP", desc: "22+ years AI expertise guiding your journey" },
              { icon: CheckCircle, title: "4+ PORTFOLIO PROJECTS", desc: "Build real AI applications on cloud platforms" }
            ].map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cloud Certifications Section */}
      <section className="py-16 bg-[#1E293B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-[#4F46E5] text-white px-6 py-3 rounded-full font-bold text-lg mb-6 shadow-xl">
              <Award className="w-6 h-6" />
              <span>Get Certified Across All Major Cloud Platforms</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
              Triple Certification Preparation
            </h2>
            <p className="text-xl md:text-2xl text-slate-700 max-w-4xl mx-auto leading-relaxed">
              One course prepares you for AI Practitioner certifications from all three major cloud providers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg", name: "AWS", cert: "AI Practitioner", services: ["SageMaker", "Bedrock", "ML Services"] },
              { logo: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Microsoft_Azure.svg", name: "Azure", cert: "AI Fundamentals", services: ["Cognitive Services", "OpenAI", "AI Platform"] },
              { logo: "https://upload.wikimedia.org/wikipedia/commons/5/51/Google_Cloud_logo.svg", name: "GCP", cert: "AI Platform", services: ["Vertex AI", "AutoML", "ML APIs"] }
            ].map((cloud, idx) => (
              <div key={idx} className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-20 mb-6 flex items-center justify-center">
                    <img 
                      src={cloud.logo} 
                      alt={cloud.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{cloud.name}</h3>
                  <p className="text-teal-700 font-semibold mb-4">{cloud.cert}</p>
                  <div className="space-y-2">
                    {cloud.services.map((service, sIdx) => (
                      <div key={sIdx} className="flex items-center gap-2 text-slate-700 text-sm">
                        <CheckCircle className="w-4 h-4 text-teal-600" />
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-2xl text-white font-bold mb-6">
              Master AI Across All Major Cloud Platforms — In One Course
            </p>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Master AI fundamentals through hands-on learning
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80" alt="AI Learning" className="w-full h-48 object-cover rounded-xl mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">Learn AI & ML Fundamentals</h3>
              <p className="text-slate-600">Master core AI concepts, machine learning algorithms, and understand how to apply them to real business problems.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80" alt="Cloud Platforms" className="w-full h-48 object-cover rounded-xl mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">Work with Cloud AI Services</h3>
              <p className="text-slate-600">Get hands-on experience with AWS, Azure, and GCP AI services. Learn to deploy models and build intelligent applications.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80" alt="Career Growth" className="w-full h-48 object-cover rounded-xl mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">Advance your career with AI</h3>
              <p className="text-slate-600">Build a strong portfolio, earn industry-recognized certifications, and position yourself for high-paying AI roles.</p>
            </div>
          </div>

          {/* Projects Section */}
          <div className="bg-teal-700 rounded-3xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-8 text-center">Build portfolio-worthy projects</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { title: "AI Chatbot with NLP", desc: "Build an intelligent chatbot using natural language processing and cloud AI services." },
                { title: "Image Classification System", desc: "Create a computer vision application that identifies and categorizes images automatically." },
                { title: "Sentiment Analysis Tool", desc: "Develop a tool that analyzes customer feedback and social media sentiment in real-time." },
                { title: "Workflow Automation Assistant", desc: "Build an AI assistant that automates repetitive business tasks and improves productivity." }
              ].map((project, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h4 className="text-xl font-bold mb-3">{project.title}</h4>
                  <p className="text-teal-100">{project.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Syllabus */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Course Syllabus</h2>
            <p className="text-xl text-slate-600">6 comprehensive modules covering everything from AI basics to certification prep</p>
          </div>

          <div className="space-y-4">
            {syllabus.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-teal-700">{item.week}</span>
                      <h3 className="text-xl font-bold text-white">{item.title}</h3>
                    </div>
                    <ul className="space-y-1">
                      {item.topics.map((topic, topicIdx) => (
                        <li key={topicIdx} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-10 py-4 rounded-xl shadow-lg transition-all duration-300">
              <Download className="w-5 h-5 inline mr-2" />
              Download complete syllabus
            </button>
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Is this course right for you?</h2>
            <p className="text-xl text-slate-600">This course is perfect for beginners and professionals looking to break into AI</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: "🎓", title: "Students & Graduates", desc: "Launch your AI career with industry-recognized certifications and hands-on experience" },
              { icon: "💼", title: "Career Changers", desc: "Transition into high-paying AI roles with comprehensive training and portfolio projects" },
              { icon: "📈", title: "Professionals Upskilling", desc: "Future-proof your career by adding AI expertise and cloud certifications to your skillset" }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-50 to-teal-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Your AI career starts here</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: "🎯", title: "Triple certification prep", desc: "Get ready for AWS, Azure, and GCP AI Practitioner certifications" },
              { icon: "💼", title: "Portfolio development", desc: "Build 4+ real AI projects to showcase to potential employers" },
              { icon: "📊", title: "Career guidance", desc: "Resume reviews, interview prep, and job search strategies included" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg text-center">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently asked questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-xl overflow-hidden border border-slate-200">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-slate-900">{faq.question}</span>
                  {openFAQ === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  )}
                </button>
                {openFAQ === faq.id && (
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                    <p className="text-slate-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#1E293B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Start your AI certification journey today
          </h2>
          <p className="text-xl text-slate-300 mb-8">Get certified as AI Practitioner across AWS, Azure & GCP</p>
          <button
            onClick={() => window.location.href = '/contact'}
            className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-12 py-5 rounded-xl shadow-2xl transition-all duration-300 text-lg"
          >
            Enroll Now →
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AICoursePage;
